// 3rd party libs
import {
    bcmul,
} from 'locutus/php/bc';
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Mixins
import walletMixin from '@shared/mixins/wallet-mixin.js';

// Helpers
import {
    formatNumberToJS,
} from '@webapp/helpers/locale.js';
import {
    formatNumber,
    truncateValueToDecimalPlaces,
} from '@shared/utils/formatter.js';

// Shared
import sharedEpicTypes from '@shared/epics/types.js';
import sharedStoreTypes from '@shared/store/types.js';
import {
    RegulationType,
} from '@shared/store/modules/wallet/types';

// Socios variables
import socios from '@webapp/socios.json';

/**
 * Mixin to handle amounts on Top Up and Send Out pages
 */
export default {
    mixins: [walletMixin],
    data: () => ({
        cryptoValue: {
            original: '',
            formatted: '0',
        },
        fiatValue: {
            original: '0',
            formatted: '0',
        },
        fiatCurrency: {
            id: '',
            name: '',
            shortName: '',
            partnerLogo: '',
            decimalDetail: 2,
        },
        cryptoCurrency: {
            id: '',
            name: '',
            shortName: '',
            partnerLogo: '',
            decimalDetail: 18,
        },
        amountMixinButtonColour: 'orange',
        isCryptoMainCurrency: false,
        originalFiatToCryptoRate: 0,
        currentRate: 0,
        currentInput: '0',
        currentRateDate: null,
        isCurrencySwitched: false,
        fiatToCrypto: {
            rate: null,
            date: null,
        },
        cryptoToFiat: {
            rate: null,
            date: null,
        },
        euroConversionRate: null,
        initialLoad: true,
        isLoading: true,
        regulatoryDailyLimit: '',
    }),
    computed: {
        ...mapGetters([
            'preferredFiatCurrency',
            'currencyCHZ',
            'currencyById',
            'currencyEuro',
            'currencyConversionRates',
            'getCurrencyConversion',
            'getLatestValidationError',
            'fiatCurrenciesList',
            'currentRegulation',
            'defaultCurrency',
            'isUsaRegulation',
            'isInternationalRegulation',
        ]),

        ...mapGetters('wallet', [
            'chzWalletLimit',
            'remainingLedgerLimit',
            'centralizedWalletCHZ',
            'hasTopupRegulatoryRestrictionLimit',
            'hasReachedRegulatoryRestrictionLimit',
            'userSendOutRegulatoryRestrictions',
            'userSendOutRegulatoryRemainingLimit',
            'hasSendOutRegulatoryRestrictions',
            'isChzAllowed',
        ]),

        conversionRateInfo() {
            const rate = this.mode === 'sendout' ? this.fiatToCrypto.rate : this.currentRate;

            return this.fiatCurrency.name && this.cryptoCurrency.name
                ? `1 ${this.fiatCurrency.name} = ${formatNumber(truncateValueToDecimalPlaces(rate, 4))} ${this.cryptoCurrency.shortName}`
                : '';
        },

        isTopupLimitReached() {
            if (!this.hasTopupRegulatoryRestrictionLimit) {
                return false;
            }

            if (this.isInternationalRegulation) {
                const type = RegulationType.TopupChz;

                return this.isTurned
                    ? this.hasReachedRegulatoryRestrictionLimit(this.fiatValue.formatted, type)
                    : this.hasReachedRegulatoryRestrictionLimit(this.cryptoValue.formatted, type);
            }

            if (this.isUsaRegulation) {
                const types = [RegulationType.TopupUsd, RegulationType.TopupUsd30Days];

                return types.some((type) => this.hasReachedRegulatoryRestrictionLimit(this.fiatValue.formatted, type));
            }
        },

        formattedFiatCurrenciesList() {
            return this.fiatCurrenciesList.map(({
                id,
                shortCode,
                decimalDetail,
                symbol,
            }) => ({
                id,
                decimalDetail,
                name: shortCode,
                shortName: shortCode,
                partnerLogo: symbol,
            })).sort((a, b) => a.name.localeCompare(b.name));
        },
    },
    watch: {
        currencyConversionRates() {
            const cryptoToFiat = this.getCurrencyConversion(this.cryptoCurrency.id, this.fiatCurrency.id);
            const fiatToCrypto = this.getCurrencyConversion(this.fiatCurrency.id, this.cryptoCurrency.id);
            const fiatToEuro = this.getCurrencyConversion(this.fiatCurrency.id, this.currencyEuro.id);

            // If both data is avaialable store the data received and set initial conversion rate
            this.cryptoToFiat = {
                rate: Number(cryptoToFiat.rate),
                date: cryptoToFiat.date,
            };

            this.fiatToCrypto = {
                rate: Number(fiatToCrypto.rate),
                date: fiatToCrypto.date,
            };

            this.originalFiatToCryptoRate = this.isTurned ? this.cryptoToFiat.rate : this.fiatToCrypto.rate;

            this.currentRate = Number(this.fiatToCrypto.rate);
            this.currentRateDate = this.fiatToCrypto.date;

            if (this.initialLoad) {
                if (this.chzAmountToBuy) {
                    this.walletMixinSetFiatValue();
                } else {
                    this.walletMixinSetCryptoValue();
                }

                this.initialLoad = false;
            }

            this.euroConversionRate = fiatToEuro.rate || null;

            // Keeping this check to ensure other scenarios aren't affected by this
            if (this.mode === 'topup') {
                this.calculateValue(null, this.isCryptoMainCurrency, true);
            }

            this.isLoading = false;
        },
    },
    async created() {
        const cryptoCurrency = {};
        const fiatCurrency = this.formattedFiatCurrenciesList.find(({ id }) => id === this.preferredFiatCurrency.id) ?? {};
        const crypto = this.currencyCHZ;

        if (crypto.id && crypto.id.length > 0) {
            cryptoCurrency.id = crypto.id;
            cryptoCurrency.name = crypto.shortCode;
            cryptoCurrency.decimalDetail = crypto.decimalDetail;
            cryptoCurrency.shortName = crypto.shortCode;
            cryptoCurrency.partnerLogo = crypto.symbol === '$CHZ' ? require('../assets/chz-logo-round.webp') : crypto.symbol;
        }

        if (this.isCryptoMainCurrency) {
            this.cryptoCurrency = fiatCurrency;
            this.fiatCurrency = cryptoCurrency;
        } else {
            this.cryptoCurrency = cryptoCurrency;
            this.fiatCurrency = fiatCurrency;
        }

        if (this.mode === 'topup') {
            this.fiatValue.original = String(this.defaultFiatValue);
            this.fiatValue.formatted = String(this.defaultFiatValue);

            const currenciesFromList = [this.cryptoCurrency.id, this.fiatCurrency.id];
            const currenciesToList = [this.fiatCurrency.id, this.cryptoCurrency.id];

            if (this.currencyEuro.id) {
                currenciesFromList.push(this.fiatCurrency.id);
                currenciesToList.push(this.currencyEuro.id);
            }

            await this.walletMixinRefreshCurrencyConversionRates(currenciesFromList, currenciesToList);
        }

        // Temporarirly use precomputed rates to obtain balance in two different currencies
        // This should be changed once multiple currencies are allowed to be fetched from BE
        if (this.mode === 'sendout') {
            this.switchCurrenciesAndConversionRate();

            await this.getCurrencies({
                chilizCurrencyId: socios.chilizCurrencyId,
            });
        }
    },
    methods: {
        ...mapActions({
            clearCurrenyConversionRates: sharedStoreTypes.SET_CURRENCY_CONVERSION_RATES,
            getCurrencies: sharedEpicTypes.GET_CURRENCIES,
        }),
        switchCurrenciesAndConversionRate() {
            this.walletMixinSwitchCurrencies();

            // Remove the value from text field to show placeholder
            if (!Number(this.fiatValue.formatted)) {
                this.fiatValue.formatted = '';
            }

            // Show 0 in the label as a "placeholder" value
            if (!Number(this.cryptoValue.formatted)) {
                this.cryptoValue.formatted = 0;
            }

            this.walletMixinSwitchCurrencyConversionRate();

            // Keeping this check to ensure other scenarios aren't affected by this
            if (this.mode === 'topup') {
                this.isCryptoMainCurrency = !this.isCryptoMainCurrency;
                this.calculateValue(null, this.isCryptoMainCurrency, true);
            }
        },
        calculateValue(event, isSwitched, updateRate = false) {
            let currentInput = 0;

            // Keep spaces off to properly calculate caret position on Android
            if (event && event.target.value) {
                event.target.value = String(event.target.value).replace(/\s/g, '');
            }

            if (this.isCryptoMainCurrency) {
                currentInput = event ? event.target.value : this.cryptoValue.original;
            } else {
                currentInput = event ? event.target.value : this.fiatValue.original;
            }

            // Prevent event firing twice
            if (typeof currentInput === 'undefined' || (String(currentInput) === this.currentInput && !updateRate)) {
                return false;
            }

            // Use String to avoid useless formatting function call to Number (eg. "12.")
            this.currentInput = String(currentInput);

            const inputtedValue = formatNumberToJS(currentInput);

            /* If we switch currencies, we just update the rate according to the currency but we do not want to replace the chz value with the one entered by
            the user since the inputted value will be formatted and we lose precision when calculating the conversion.
            */
            if (isSwitched && inputtedValue.length > 1) {
                this.walletMixinSwitchCurrencyConversionRate();
            } else {
                // Store the original (not formatted) value as inputted by user (if currency is not switched)
                this.fiatValue.original = inputtedValue;
                this.fiatValue.formatted = truncateValueToDecimalPlaces(this.fiatValue.original);

                if (!Number(this.fiatValue.formatted)) {
                    this.fiatValue.formatted = '';
                }
                this.$set(this.cryptoValue, 'formatted', this.cryptoValue.formatted);
            }

            /* If the value entered by the user is invalid or the calculation of the amount multiplied by the rate is 0, set fiat value to 0.
            This will eliminate the NaN issue.
            */
            if (isNaN(this.fiatValue.original) || ((Number(this.fiatValue.original) * Number(this.currentRate)) === 0)) {
                this.cryptoValue.original = 0;
                /**
                 * Check if doesn't have currency switched or chz value not assigned or empty.
                 * Old Bug: Doesn't show the value when switched if is single digit.
                 */
            } else if (!this.isCurrencySwitched || !this.cryptoValue.original || !Number(this.cryptoValue.original)) {
                this.cryptoValue.original = bcmul(this.fiatValue.original, this.currentRate, this.cryptoCurrency.decimalDetail);
            }

            this.cryptoValue.formatted = truncateValueToDecimalPlaces(this.cryptoValue.original);
            this.isCurrencySwitched = false;

            if (this.mode === 'sendout') {
                this.handleSendOutRegulatoryLimits();
            }
        },
        handleSendOutRegulatoryLimits() {
            if (!this.hasSendOutRegulatoryRestrictions) {
                return false;
            }

            const isLimitReached = this.fiatValue.original && this.userSendOutRegulatoryRemainingLimit &&
                (Number(this.fiatValue.original) > Number(this.userSendOutRegulatoryRemainingLimit));

            this.regulatoryDailyLimit = isLimitReached
                ? this.userSendOutRegulatoryRestrictions.limitAmount
                : 0;
        },
    },
};